import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';

export interface DataTableColumn<T> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  render?: (row: T, index: number) => React.ReactNode;
  sx?: SxProps<Theme>;
}

export interface DataTablePagination {
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  pagination?: DataTablePagination;
  rowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  sx?: SxProps<Theme>;
}

function DataTable<T>({
  columns,
  data,
  loading = false,
  emptyState,
  pagination,
  rowKey,
  onRowClick,
  stickyHeader = false,
  maxHeight,
  sx,
}: DataTableProps<T>) {
  const { theme } = useTheme();
  const tableTheme = theme.components?.table;

  const headerBg = tableTheme?.header ?? theme.gray[50];
  const borderColor = tableTheme?.border ?? theme.components.card.border;
  const hoverBg = tableTheme?.hover ?? theme.components.card.hover;
  const stripeBg = tableTheme?.stripe ?? 'transparent';

  const colSpan = columns.length;

  return (
    <Box data-testid="data-table">
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          backgroundColor: theme.components.card.background,
          overflowX: 'auto',
          ...(maxHeight ? { maxHeight } : {}),
          ...sx,
        }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align ?? 'left'}
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    backgroundColor: headerBg,
                    color: theme.text,
                    borderBottom: `1px solid ${borderColor}`,
                    whiteSpace: 'nowrap',
                    minWidth: col.minWidth,
                    ...col.sx,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 6,
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: theme.primary }} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    {emptyState ?? (
                      <Typography sx={{ color: theme.gray[400] }}>
                        No data available
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => (
                <TableRow
                  key={rowKey ? rowKey(row, idx) : idx}
                  hover
                  onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor: idx % 2 === 1 ? stripeBg : 'transparent',
                    '&:hover': { backgroundColor: hoverBg },
                    '& td': {
                      borderBottom: `1px solid ${borderColor}`,
                      color: theme.text,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} align={col.align ?? 'left'}>
                      {col.render
                        ? col.render(row, idx)
                        : String((row as Record<string, unknown>)[col.id] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && !loading && data.length > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
          onRowsPerPageChange={
            pagination.onRowsPerPageChange
              ? (e) => pagination.onRowsPerPageChange!(parseInt(e.target.value, 10))
              : undefined
          }
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? [10, 25, 50]}
          sx={{
            color: theme.text,
            borderTop: `1px solid ${borderColor}`,
            '.MuiTablePagination-selectIcon': { color: theme.gray[400] },
          }}
          data-testid="data-table-pagination"
        />
      )}
    </Box>
  );
}

export default DataTable;
